import axios from 'axios'
import { useAuthStore } from '@/stores/auth.js'
import { createGenericStore } from '@/stores/utils/genericStore.js'

const roomFromResponse = (data) => data?.data ?? data

const directSecureTransferStoreOptions = {
  state: () => ({
    room: null,
    peers: [],
    isSubscribed: false,
    roomChannelPrefix: 'direct-secure-transfer.room',
  }),

  getters: {
    isCreator: (state) => state.room?.role === 'creator',
    isRecipient: (state) => state.room?.role === 'recipient',
    isParticipant: (state) => ['creator', 'recipient'].includes(state.room?.role),
  },

  actions: {
    async createRoom() {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.post(this.api)
        this.room = roomFromResponse(data)

        return this.room
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async fetchRoom(publicId) {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.get(`${this.api}/${publicId}`)
        this.room = roomFromResponse(data)

        return this.room
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async joinRoom(publicId) {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.post(`${this.api}/${publicId}/join`)
        this.room = roomFromResponse(data)

        return this.room
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async sendSignal(type, payload = {}) {
      if (!this.room?.public_id) {
        throw new Error('A transfer room is required before signaling.')
      }

      await axios.post(`${this.api}/${this.room.public_id}/signals`, {
        type,
        payload,
      })
    },

    subscribeToRoom(handlers = {}) {
      if (!window.Echo || !this.room?.public_id || this.isSubscribed) {
        return
      }

      const channelName = `${this.roomChannelPrefix}.${this.room.public_id}`
      const currentUserId = Number(useAuthStore().user?.id)

      window.Echo.join(channelName)
        .here((users) => {
          this.peers = users
          handlers.presenceChanged?.(users)
          this.sendSignal('peer-ready', handlers.transferId?.() ? {
            transfer_id: handlers.transferId(),
          } : {}).catch((error) => handlers.signalFailed?.(error))
        })
        .joining((user) => {
          this.peers = [...this.peers.filter((peer) => Number(peer.id) !== Number(user.id)), user]
          handlers.peerAvailable?.(user)
          handlers.presenceChanged?.(this.peers)
        })
        .leaving((user) => {
          this.peers = this.peers.filter((peer) => Number(peer.id) !== Number(user.id))
          handlers.peerLeft?.(user)
          handlers.presenceChanged?.(this.peers)
        })
        .listen('.Umisis\\DirectSecureTransfer\\Events\\PeerJoinedEvent', (event) => {
          if (Number(event?.peer?.id) === currentUserId) {
            return
          }

          handlers.peerJoined?.(event)
        })
        .listen('.Umisis\\DirectSecureTransfer\\Events\\DirectSecureTransferSignalEvent', (event) => {
          if (Number(event?.sender_user_id) === currentUserId) {
            return
          }

          handlers.signalReceived?.(event)
        })

      this.isSubscribed = true
    },

    unsubscribeFromRoom() {
      if (window.Echo && this.room?.public_id) {
        window.Echo.leave(`${this.roomChannelPrefix}.${this.room.public_id}`)
      }

      this.peers = []
      this.isSubscribed = false
    },

    resetRoom() {
      this.unsubscribeFromRoom()
      this.room = null
      this.error = null
    },
  },
}

export const useDirectSecureTransferStore = createGenericStore(
  'direct-secure-transfer',
  '/api/v1/direct-secure-transfers',
  directSecureTransferStoreOptions
)
